import { MidiDevice } from "../model/MidiDevice.js";

export type MixerFaderInitialState = "sinus" | "optimal" | "loud" | "quite" | "random" | "suboptimal";

export type MixerPosition = "center" | "bottom" | "docked" | "bottom-outer" | "relative";

export type MixerStyle = "real" | "comic";

export interface MixerFaderState {
    index: number;
    volume: number;
}

export interface MixerOptions {
    container: HTMLElement;
    faderCount?: number;
    slidable?: boolean;
    midi?: MidiDevice;
    visible?: boolean;
    position?: MixerPosition;
    draggable?: boolean;
    mixer?: Mixer;
    style?: MixerStyle;
    faderInitialState?: MixerFaderInitialState;
}

export class Mixer {

    public readonly onChange = e5.Signal.create<[MixerFaderState[]]>();
    public readonly onChangeFader = e5.Signal.create<[MixerFaderState]>();

    public readonly faders: MixerFader[] = [];

    private _element: HTMLDivElement;
    private _options: MixerOptions;

    constructor(options: MixerOptions) {

        options = e5.object.merge({
            faderCount: 16,
            visible: true,
            position: "center",
            style: "comic",
            draggable: false,
            slidable: !(options.midi || options.mixer),
            faderInitialState: "optimal"
        } as MixerOptions, options);
        this._options = options;

        this._element = document.createElement("div");

        this.init();
    }

    private async init() {

        const options = this._options;

        this._element.className = "mixer";
        this._element.classList.add(
            "mixer--pos-" + options.position,
            "mixer--style-" + options.style);
        this._element.innerHTML = `
        <div class="mixer__header"></div>
        <div class="mixer__fader-list"></div>
        `;

        if (options.visible === false) {
            this._element.classList.add("mixer--hidden");
        }

        if (options.container) {
            options.container.append(this._element);
        }

        if (options.draggable) {
            const actor = new e5.Drag(this._element, {
                clamp: {
                    parent: options.container,
                    tweenTime: 0.2,
                    boundsParent: {
                        top: 120,
                        right: 20,
                        bottom: 20,
                        left: 20
                    }
                }
            });
            actor.target = this._element;
            actor.element = this._element.querySelector(".mixer__header");
        } else {
            this._element.querySelector(".mixer__header").classList.add("e5-hidden");
        }

        var faderInitialState = this._options.faderInitialState;
        for (let i = 0; i < options.faderCount; ++i) {

            let volume = 0.5;
            if (options.midi) {
                volume = options.midi.getVolume(i, 0.5);
            }
            else if (options.mixer) {
                volume = options.mixer.getVolume(i, 0.5);
            } else {
                if (faderInitialState === "sinus") {
                    volume = ((Math.sin(((i * 4) / (options.faderCount - 1)) * Math.PI)) + 1) * 0.5;
                } else if (faderInitialState === "optimal") {
                    volume = 0.5;
                } else if (faderInitialState === "loud") {
                    volume = 1;
                } else if (faderInitialState === "quite") {
                    volume = 0;
                } else if (faderInitialState === "random") {
                    volume = Math.random();
                } else if (faderInitialState === "suboptimal") {
                    volume = i == 0 ? 0.2 : 0.5;
                } else {
                    volume = 0.5;
                }
            }

            const fader = new MixerFader({
                container: this._element.querySelector(".mixer__fader-list"),
                index: i,
                slidable: options.slidable,
                volume: volume
            });
            fader.onChange.add((state, fader) => {
                this.onChangeFader.dispatch(state);
                this.onChange.dispatch(
                    this.faders.map((fader) => {
                        return {
                            index: fader.index,
                            volume: fader.volume
                        }
                    })
                );
            });
            this.faders.push(fader);
        }

        if (options.midi) {
            options.midi.onChangeFader.add((f) => this.applyFaderState(f));
        }

        if (options.mixer) {
            options.mixer.onChangeFader.add((f) => this.applyFaderState(f));
        }
    }

    private applyFaderState(state: MixerFaderState): void {
        const fader = this.faders[state.index];
        if (!fader) {
            console.warn("Could not find fader at index", state.index);
            return;
        }
        fader.volume = state.volume;
    }

    public getVolume(index: number, fallback: number = 0.5): number {
        const volume = this.getFader(index)?.volume;
        return volume === null ? fallback : volume;
    }

    public getFader(index: number): MixerFader {
        return this.faders[index];
    }

    public get visible(): boolean {
        return !this._element.classList.contains("mixer--hidden");
    }

    public set visible(value: boolean) {
        this._element.classList.toggle("mixer--hidden", !value);
    }
}

interface MixerFaderOptions {
    container: HTMLElement;
    volume?: number;
    index: number;
    slidable: boolean;
}

class MixerFader {
    public readonly element: HTMLDivElement;

    public onChange = e5.Signal.create<[MixerFaderState, MixerFader]>();

    private _options: MixerFaderOptions;
    private _volume: number = -1;

    constructor(options: MixerFaderOptions) {

        options = e5.object.merge({
            volume: 0.5
        } as MixerFaderOptions, options);
        this._options = options;

        "".repeat(5).split("").map(() => { });

        this.element = document.createElement("div");
        this.element.className = "mixer__fader";
        if (options.slidable) {
            this.element.classList.add("mixer__fader--slidable");
        }
        this.element.innerHTML = `
        <div class="mixer__fader-wrapper">
            <div class="mixer__fader-line-list">
            ${Array(5).fill(0).map(
            () => {
                return `<div class="mixer__fader-line"></div>`;
            }).join("")
            }
            </div>
            <div class="mixer__fader-track">
                <div class="mixer__fader-thumb-track">
                    <div class="mixer__fader-thumb"></div>
                </div>
            </div>
        </div>
        `;

        this.volume = options.volume;

        if (options.slidable) {
            this.setTouchControl();
        }

        this._options.container.append(this.element);
    }

    private setTouchControl(): void {
        const thumbTrack = this.element.querySelector<HTMLElement>(".mixer__fader-thumb-track");
        const wrapper = this.element.querySelector<HTMLElement>(".mixer__fader-wrapper");
        const updatePos = () => {
            const local = e5.Transform.toLocal(thumbTrack, a.pointer.x, a.pointer.y);
            this.volume = 1 - e5.Calc.clamp(local.y / thumbTrack.offsetHeight, 0, 1);
        };

        const a = new e5.Actor(wrapper);
        a.onMove.add(updatePos);
        a.onDown.add(updatePos);

        wrapper.addEventListener("wheel", (e) => {
            const delta = 0.05;
            this.volume += e.deltaY < 0 ? delta : -delta;
        }, {
            passive: true
        });
    }

    public get index(): number {
        return this._options.index;
    }

    public get volume(): number {
        return this._volume;
    }

    public set volume(value: number) {
        value = e5.Calc.clamp(value, 0, 1);
        if (this._volume === value) {
            return;
        }
        this._volume = value;

        const thumb = this.element.querySelector<HTMLElement>(".mixer__fader-thumb");
        thumb.style.top = ((1 - value) * 100).toFixed(6) + "%";

        this.onChange.dispatch(
            {
                index: this._options.index,
                volume: value
            },
            this);
    }
}